import { locateProxy } from '#~/agent/index.js';
import { currentLimits, limitCanUseCloudMode } from '#~/api/billing/limits.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { useMultiDeviceStateQuery, useMultiProxyStateQuery } from '#~/statequery/hook.js';
import type { DeviceEgressSelectorType } from '#~/statequery/selectors/device-egress.js';
import { DeviceRTMPDestinationsStatusSelector } from '#~/statequery/selectors/device-rtmp-destinations.js';
import { useEgressErrorStore } from '#~/store/index.js';
import { Button } from '@studio/design-system/button';
import { Icon } from '@studio/design-system/icon';
import { Ellipses } from '@studio/design-system/label';
import { usePrompt } from '@studio/design-system/prompt';
import * as Select from '@studio/design-system/select';
import { getCurrentVendor } from '@studio/design-system/themes/vendor.js';
import type { Device } from '@studio/models/device';
import { StatusEgressState, type StatusDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCodecOptionLabel, getCodecWarning } from '../components/codec-warning.js';
import { SourceCard } from '../components/source-card.js';
import {
  CLOUD_USAGE_LIMIT_MESSAGE,
  type EncoderBillingLockSnapshot,
  isBillingLimitError,
  isDeviceEncoderBillingLocked,
  selectEncoderBillingLock
} from './billing-limit.js';
import type { Destination } from './store/destinations.js';
import {
  useSelectedSource,
  useSetSelectedSource,
  useStartStreaming,
  useStopStreaming
} from './store/destinations.js';
import { getErrorMessage, getProfileConfig, getRTMPConfig } from './utils.js';

// Vendor-specific background colors for RTMP destination cards
const RTMP_CARD_BACKGROUNDS = {
  iris: '#23283A',
  ptzoptics: '#2A251F'
} as const;

export const DestinationCard = ({
  destination,
  videoSources,
  onEditDestination,
  streamProfile,
  ...props
}: {
  destination: Destination;
  videoSources: {
    id: string;
    name: string;
    egress: StatusDevice['egress'];
    type: 'video' | 'audio';
    videoCodec: string | undefined;
  }[];
  onEditDestination: (destination: Destination) => void;
  streamProfile?: string | undefined;
} & { 'data-stag'?: string }): ReactElement => {
  const selectedSource = useSelectedSource(destination.id);
  const setSelectedSource = useSetSelectedSource();

  const [isLoading, setIsLoading] = useState(false);
  const [operationType, setOperationType] = useState<'start' | 'stop' | null>(null);
  // Full H265 (etc.) wording shown under Assign source when the operator tries a
  // denied codec. Kept off the global toast stack — Didier QA 2026-09-17: that
  // message was appearing top-left of the app instead of in the RTMP tab.
  // Per rule: stay-in-scope (ai.knowledge/global).
  const [blockedPickWarning, setBlockedPickWarning] = useState<string | undefined>();

  // Get destination-specific status state information
  const destinationStatuses = useMultiDeviceStateQuery(DeviceRTMPDestinationsStatusSelector);
  // Subscribe via central dispatch so Start Streaming disables/enables when billingLocked flips.
  const encoderBillingLocks =
    useMultiProxyStateQuery<EncoderBillingLockSnapshot>(selectEncoderBillingLock);

  const startStreaming = useStartStreaming();
  const stopStreaming = useStopStreaming();
  const prompt = usePrompt();
  const { dismiss, isDismissed, clear } = useEgressErrorStore();

  const handleSourceChange = useCallback(
    (value: Device['id']) => {
      const next = videoSources.find((s) => s.id === value);
      // Soft-reject denied codecs: keep the option clickable so the reason can
      // land in the card error slot (same story as SourceCard onBlockedSelection
      // for Recording/NDI), but never assign the source. Items stay enabled so
      // the click reaches onValueChange — Radix swallows clicks on disabled items.
      const warning = getCodecWarning('rtmp', next?.videoCodec);
      if (warning) {
        setBlockedPickWarning(warning);
        return;
      }
      setBlockedPickWarning(undefined);
      setSelectedSource(destination.id, value);
    },
    [destination.id, setSelectedSource, videoSources]
  );

  const source = useMemo(() => {
    if (!selectedSource) return null;

    return videoSources.find((s) => s.id === selectedSource);
  }, [selectedSource, videoSources]);

  const isSelectedSourceBillingLocked = isDeviceEncoderBillingLocked(
    encoderBillingLocks,
    selectedSource
  );
  const isCloudUsageBlocked =
    isSelectedSourceBillingLocked || (currentLimits !== null && !limitCanUseCloudMode());

  const getCloudUsageLimitMessage = useCallback((): string | null => {
    const effectiveDeviceId = (selectedSource || videoSources[0]?.id) as Device['id'] | undefined;
    if (!effectiveDeviceId) {
      return null;
    }

    // Action-time re-read: lock can flip between the last render and this click.
    const billingLocked = !!locateProxy(effectiveDeviceId)?.state.control?.encoder?.billingLocked;
    const billingLimitsLoaded = currentLimits !== null;
    const cloudModeBlockedByLimits = billingLimitsLoaded && !limitCanUseCloudMode();

    if (billingLocked || cloudModeBlockedByLimits) {
      return CLOUD_USAGE_LIMIT_MESSAGE;
    }

    return null;
  }, [selectedSource, videoSources]);

  // ####### DESTINATION-SPECIFIC STATES #######
  const destinationState = useMemo(() => {
    if (!selectedSource || !destination.id) return null;

    // Get destination state for the selected source
    const deviceDestinations = destinationStatuses[selectedSource];
    if (!deviceDestinations?.destinations) return null;

    return deviceDestinations.destinations[destination.id] || null;
  }, [selectedSource, destination.id, destinationStatuses]);

  // ####### ACTIVE STREAMING DETECTION ACROSS ALL DEVICES #######
  const activeStreamingInfo = useMemo(() => {
    if (!destination.id)
      return { isStreaming: false, activeDeviceId: null, destinationState: null };

    // Check all devices for this destination's streaming status
    for (const [deviceId, deviceDestinations] of Object.entries(destinationStatuses)) {
      const destState = deviceDestinations?.destinations?.[destination.id];
      if (destState?.state === StatusEgressState.EGRESS_STATE_ACTIVE) {
        return {
          isStreaming: true,
          activeDeviceId: deviceId as Device['id'],
          destinationState: destState
        };
      }
    }

    return { isStreaming: false, activeDeviceId: null, destinationState: null };
  }, [destination.id, destinationStatuses]);

  // console.log('___destinationState', destinationState);
  // console.log('___activeStreamingInfo', activeStreamingInfo);

  // Auto-select the actively streaming device if no source is selected
  useEffect(() => {
    if (!selectedSource && activeStreamingInfo.isStreaming && activeStreamingInfo.activeDeviceId) {
      setSelectedSource(destination.id, activeStreamingInfo.activeDeviceId);
    }
  }, [
    selectedSource,
    activeStreamingInfo.isStreaming,
    activeStreamingInfo.activeDeviceId,
    destination.id,
    setSelectedSource
  ]);

  const isStreaming = useMemo(() => {
    // First check if we have a selected source and its state
    if (destinationState?.state === StatusEgressState.EGRESS_STATE_ACTIVE) {
      return true;
    }

    // If no selected source or not streaming on selected source, check across all devices
    return activeStreamingInfo.isStreaming;
  }, [destinationState, activeStreamingInfo.isStreaming]);

  // Get the effective source for display (either selected or actively streaming)
  const effectiveSource = useMemo(() => {
    // First try to use the selected source
    if (selectedSource && source) {
      return source;
    }

    // If no selected source but we have active streaming, find the active device
    if (activeStreamingInfo.isStreaming && activeStreamingInfo.activeDeviceId) {
      return videoSources.find((s) => s.id === activeStreamingInfo.activeDeviceId) || null;
    }

    return null;
  }, [
    selectedSource,
    source,
    activeStreamingInfo.isStreaming,
    activeStreamingInfo.activeDeviceId,
    videoSources
  ]);

  const isStarting = useMemo(() => {
    // Check selected source first
    if (destinationState?.state === StatusEgressState.EGRESS_STATE_STARTING) {
      return true;
    }

    // If no selected source, check active streaming info
    return activeStreamingInfo.destinationState?.state === StatusEgressState.EGRESS_STATE_STARTING;
  }, [destinationState, activeStreamingInfo.destinationState]);

  const isStopping = useMemo(() => {
    // Check selected source first
    if (destinationState?.state === StatusEgressState.EGRESS_STATE_STOPPING) {
      return true;
    }

    // If no selected source, check active streaming info
    return activeStreamingInfo.destinationState?.state === StatusEgressState.EGRESS_STATE_STOPPING;
  }, [destinationState, activeStreamingInfo.destinationState]);

  const isOperationInProgress = useMemo(() => {
    if (isStarting || isStopping) return true;

    // Get the effective destination state (either from selected source or active streaming)
    const effectiveDestinationState = destinationState || activeStreamingInfo.destinationState;

    if (isLoading) {
      if (operationType === 'start') {
        return (
          !isStreaming &&
          effectiveDestinationState?.state !== StatusEgressState.EGRESS_STATE_FAILED &&
          effectiveDestinationState?.state !== StatusEgressState.EGRESS_STATE_FAILED_TO_START
        );
      }

      if (operationType === 'stop') {
        return isStreaming;
      }
    }

    return false;
  }, [
    isStarting,
    isStopping,
    isLoading,
    operationType,
    isStreaming,
    destinationState,
    activeStreamingInfo.destinationState
  ]);
  // ####### DESTINATION-SPECIFIC STATES #######

  useEffect(() => {
    if (!isLoading || !operationType) return;

    // Get the effective destination state (either from selected source or active streaming)
    const effectiveDestinationState = destinationState || activeStreamingInfo.destinationState;

    if (operationType === 'start') {
      // Reset when stream successfully starts or fails to start
      if (
        isStreaming ||
        effectiveDestinationState?.state === StatusEgressState.EGRESS_STATE_FAILED ||
        effectiveDestinationState?.state === StatusEgressState.EGRESS_STATE_FAILED_TO_START
      ) {
        setIsLoading(false);
        setOperationType(null);
      }
    } else if (operationType === 'stop') {
      // Reset when stream successfully stops (not streaming and not in transition states)
      if (!isStreaming && !isStarting && !isStopping) {
        setIsLoading(false);
        setOperationType(null);
      }
    }
  }, [
    isLoading,
    operationType,
    isStreaming,
    isStarting,
    isStopping,
    destinationState,
    activeStreamingInfo.destinationState
  ]);

  const handleStartStreaming = useCallback(async () => {
    // Use selected source or fall back to the first available video source
    const effectiveDeviceId = selectedSource || videoSources[0]?.id;
    if (!effectiveDeviceId) return;
    const assigned = videoSources.find((s) => s.id === effectiveDeviceId);
    if (getCodecWarning('rtmp', assigned?.videoCodec)) return;
    clear(destination.id, 'rtmp');

    const cloudUsageLimitMessage = getCloudUsageLimitMessage();
    if (cloudUsageLimitMessage) {
      prompt.show({
        title: 'Cloud usage limit reached',
        message: cloudUsageLimitMessage,
        okay: { label: 'OK' }
      });
      return;
    }

    setIsLoading(true);
    setOperationType('start');

    const timeoutId = setTimeout(() => {
      // Check current state to avoid stale closure issues
      setIsLoading((currentLoading) => {
        setOperationType((currentOperation) => {
          if (currentLoading && currentOperation === 'start') {
            prompt.show({
              title: 'Operation Timeout',
              message: 'Starting stream is taking longer than expected. Please try again.',
              okay: { label: 'OK' }
            });
            return null;
          }
          return currentOperation;
        });
        return currentLoading && operationType === 'start' ? false : currentLoading;
      });
    }, 10_000);

    try {
      const profileConfig = streamProfile
        ? getProfileConfig(streamProfile, effectiveDeviceId as Device['id'])
        : undefined;

      await startStreaming(effectiveDeviceId as Device['id'], destination, profileConfig);
    } catch (error) {
      if (isBillingLimitError(error)) {
        prompt.show({
          title: 'Cloud usage limit reached',
          message: CLOUD_USAGE_LIMIT_MESSAGE,
          okay: { label: 'OK' }
        });
        setIsLoading(false);
        setOperationType(null);
        return;
      }

      prompt.show({
        title: 'Error',
        message: 'Failed to start streaming. Please try again.',
        okay: { label: 'OK' }
      });
      setIsLoading(false);
      setOperationType(null);
    } finally {
      clearTimeout(timeoutId);
    }
  }, [
    selectedSource,
    videoSources,
    // ENG-4557 Bug 2 fix: depend on the whole `destination`, not just its id.
    // Editing a destination changes url/streamKey but NOT its id, so an id-only
    // dep left this callback pinned to a STALE destination — START fired the
    // PREVIOUS url ("off by one edit"). immer gives the edited destination a
    // fresh object ref, so depending on `destination` recreates the callback
    // with current values. Per code-comments-required (ai.knowledge/global).
    destination,
    startStreaming,
    prompt,
    operationType,
    streamProfile,
    getCloudUsageLimitMessage,
    clear
  ]);

  const handleStopStreaming = useCallback(async () => {
    // Use selected source or actively streaming device
    const effectiveDeviceId = selectedSource || activeStreamingInfo.activeDeviceId;
    if (!effectiveDeviceId) return;

    prompt.confirm({
      title: 'Stop Streaming',
      message: `Are you sure you want to stop streaming to ${destination.nickName}?`,
      okay: {
        variant: 'danger',
        label: 'Stop Streaming'
      },
      async onConfirm(confirmed) {
        if (!confirmed) return;

        setIsLoading(true);
        setOperationType('stop');

        const timeoutId = setTimeout(() => {
          // Check current state to avoid stale closure issues
          setIsLoading((currentLoading) => {
            setOperationType((currentOperation) => {
              if (currentLoading && currentOperation === 'stop') {
                prompt.show({
                  title: 'Operation Timeout',
                  message:
                    'Stopping stream is taking longer than expected. The operation may still be in progress.',
                  okay: { label: 'OK' }
                });
                return null;
              }
              return currentOperation;
            });
            return currentLoading && operationType === 'stop' ? false : currentLoading;
          });
        }, 10_000);

        try {
          const profileConfig = streamProfile
            ? getProfileConfig(streamProfile, effectiveDeviceId as Device['id'])
            : undefined;

          await stopStreaming(effectiveDeviceId as Device['id'], destination, profileConfig);

          // Show success notification for stop operation
          prompt.show({
            title: 'Success',
            message: `Successfully stopped streaming to ${destination.nickName}.`,
            okay: { label: 'OK' }
          });
        } catch (error) {
          console.error('___Failed to stop streaming:', error);
          prompt.show({
            title: 'Error',
            message: 'Failed to stop streaming. The stream may still be active. Please try again.',
            okay: { label: 'OK' }
          });
          // Only reset state on error - let useEffect handle success cases
          setIsLoading(false);
          setOperationType(null);
        } finally {
          clearTimeout(timeoutId);
        }
      }
    });
  }, [
    selectedSource,
    activeStreamingInfo.activeDeviceId,
    // ENG-4557 Bug 2: same stale-closure fix as start — depend on the whole
    // destination so an edit (new url/streamKey, same id) recreates this callback
    // instead of leaving it pinned to the previous destination object.
    destination,
    stopStreaming,
    prompt,
    isLoading,
    operationType,
    streamProfile
  ]);

  const handleOnClick = () => {
    onEditDestination(destination);
  };

  // Get vendor-specific background color
  const cardBackgroundColor = useMemo(() => {
    const vendor = getCurrentVendor();
    return RTMP_CARD_BACKGROUNDS[vendor] || RTMP_CARD_BACKGROUNDS.iris;
  }, []);
  const determineStreamState = useMemo(() => {
    // Prioritize backend states first
    if (isStopping) return 'Stopping...';
    if (isStarting) return 'Starting...';

    // Then handle local loading states
    if (isLoading && operationType === 'start') return 'Starting...';
    if (isLoading && operationType === 'stop') return 'Stopping...';

    return 'Start Streaming';
  }, [isStarting, isStopping, isLoading, operationType]);

  const assignedCodecWarning = getCodecWarning('rtmp', source?.videoCodec);
  // Prefer the proactive pick/assignment reason over a backend failure string.
  const cardCodecWarning = assignedCodecWarning ?? blockedPickWarning;
  const streamingCodecWarning = getCodecWarning('rtmp', effectiveSource?.videoCodec);
  const rtmpReactiveError = getErrorMessage(
    (destinationState || activeStreamingInfo.destinationState)?.state,
    'rtmp',
    (destinationState || activeStreamingInfo.destinationState)?.error
  );
  const rtmpVisibleError = isDismissed(destination.id, 'rtmp', rtmpReactiveError)
    ? undefined
    : rtmpReactiveError;

  return (
    <div
      className='md:flex-row shadow-sm mb-6 flex w-full max-w-[488px] items-center justify-between gap-4 rounded-8 p-8'
      style={{ backgroundColor: cardBackgroundColor }}
      data-stag='rtmp.destinations.card.container'
      {...props}
    >
      {!isStreaming ? (
        <>
          {/* Icon and source select */}
          <div className='flex w-auto gap-4' data-stag='rtmp.destinations.card.config_section'>
            <Button
              className='m-6 flex h-28 w-28 cursor-pointer items-center justify-center rounded-4 bg-base-200 transition-colors hover:bg-base-300'
              onClick={handleOnClick}
              disabled={isOperationInProgress}
              aria-label='Edit destination settings'
              data-stag='rtmp.destinations.card.edit_button'
            >
              <Icon className='text-lg' icon='settings' />
            </Button>
            <div className='flex flex-col gap-4'>
              <p className='max-w-[200px] truncate text-md font-medium leading-[14px] text-light'>
                {destination.nickName}
              </p>
              <Select.Root
                value={selectedSource}
                onValueChange={handleSourceChange}
                size='sm'
                disabled={isOperationInProgress}
              >
                {/* uniqueID = destination.id so two cards never share one selector STag. */}
                <Select.Trigger
                  className='w-fit max-w-[200px] border border-[#2D3346] pr-16'
                  {...STagNew(Sel.ProductionOutput, 'rtmp-source-selector', destination.id)}
                >
                  <Ellipses
                    className='max-w-[200px]'
                    title={videoSources?.find((s) => s.id === videoSources[0]!.id)?.name}
                  >
                    <Select.Value placeholder={'Assign source'} />
                  </Ellipses>
                </Select.Trigger>
                {/* position=popper: item-aligned Select inside Dialog.Sheet
                    (translateX slide) was painting the H265 option list at the
                    app top-left instead of under Assign source (Didier QA).
                    Same pattern as room/user-invite Selects. */}
                <Select.Content position='popper' side='bottom'>
                  {videoSources?.map((s) => {
                    const itemWarning = getCodecWarning('rtmp', s.videoCodec);
                    return (
                      <Select.Item
                        key={s.id}
                        value={s.id}
                        // Not Radix-disabled: clicks must reach handleSourceChange
                        // so the full warning can render in the card error slot.
                        aria-disabled={itemWarning ? 'true' : undefined}
                        className={
                          itemWarning
                            ? 'max-w-[200px] cursor-not-allowed text-sm text-muted'
                            : 'max-w-[200px] text-sm text-light'
                        }
                        {...STagNew(Sel.ProductionOutput, 'rtmp-source-option', s.id)}
                      >
                        {/* Label (suffix included) comes from codec-warning.ts so the
                            codec is never named a second time in this file. */}
                        {getCodecOptionLabel('rtmp', s.name, s.videoCodec)}
                      </Select.Item>
                    );
                  })}
                </Select.Content>
              </Select.Root>
              {/* Same error slot as SourceCard so a FAILED destination still explains itself. */}
              {(cardCodecWarning || rtmpVisibleError) && (
                <p
                  className='flex max-w-[200px] items-center gap-6 rounded-4 border-1 border-error p-12 text-sm'
                  {...DataSTag(Sel.ProductionSourceCard, 'error', destination.id)}
                >
                  <i className='i-ion-warning-outline text-xl text-error' />
                  <span className='grow'>{cardCodecWarning ?? rtmpVisibleError}</span>
                  {rtmpVisibleError && !cardCodecWarning && (
                    <button
                      type='button'
                      {...STagNew(Sel.ProductionSourceCard, 'dismiss_error', destination.id)}
                      aria-label='Dismiss error'
                      className='shrink-0 text-muted hover:text-foreground'
                      onClick={() => dismiss(destination.id, 'rtmp', rtmpVisibleError)}
                    >
                      <i className='i-lucide-x text-lg' />
                    </button>
                  )}
                </p>
              )}
            </div>
          </div>
          {/* Streaming actions */}
          <div
            className='flex w-full items-center justify-end gap-4 p-12'
            data-stag='rtmp.destinations.card.actions'
          >
            {/* uniqueID = destination.id so Playwright can target one Start button. */}
            <Button
              size='lg'
              variant='secondary'
              onClick={handleStartStreaming}
              {...STagNew(Sel.ProductionOutput, 'rtmp-start-streaming', destination.id)}
              disabled={
                isOperationInProgress ||
                !selectedSource ||
                isCloudUsageBlocked ||
                Boolean(assignedCodecWarning)
              }
            >
              {determineStreamState}
            </Button>
          </div>
        </>
      ) : (
        <SourceCard
          className='w-full'
          stagId={destination.id}
          name={effectiveSource?.name ?? ''}
          isActive={isStreaming}
          type={'video'}
          unselectableReason={streamingCodecWarning}
          error={rtmpVisibleError}
          onDismissError={
            rtmpVisibleError ? () => dismiss(destination.id, 'rtmp', rtmpVisibleError) : undefined
          }
          config={getRTMPConfig(
            effectiveSource as DeviceEgressSelectorType,
            (destinationState || activeStreamingInfo.destinationState)!
          )}
          selected={!!selectedSource || !!activeStreamingInfo.activeDeviceId}
          onSelection={() => {}}
          handleStopStreaming={handleStopStreaming}
          isSelectable={true}
          isStopOperationInProgress={isStopping}
          destinationTitle={destination.nickName}
          hideIcon={true}
        />
      )}
    </div>
  );
};
